import api from './index';
import { withRuntimeUrl } from './runtimeConfig';

const withCreativeServerUrl = (buildRequest: (arg: any) => any) =>
  withRuntimeUrl(
    'creativeServerUrl',
    'Creative server URL not configured in ETCD',
    buildRequest,
  );

const creativeServerApi = api.injectEndpoints({
  endpoints: build => ({
    appNames: build.query({
      queryFn: withCreativeServerUrl(({ keyword, country, os }) => ({
        method: 'GET',
        url: '/v1/on-demand/fetch-app-names',
        params: {
          keyword,
          country,
          os,
        },
      })),
    }),
    adslots: build.query({
      queryFn: withCreativeServerUrl(
        ({ height, width, tvServerEnvironment }) => ({
          method: 'GET',
          url: '/v1/on-demand/fetch-adslots',
          params: {
            height,
            width,
            ...(tvServerEnvironment ? { tvServerEnvironment } : {}),
          },
        }),
      ),
    }),
    contentPartners: build.query({
      queryFn: withCreativeServerUrl(({ keyword }) => ({
        method: 'GET',
        url: '/v1/on-demand/fetch-content-partners',
        params: {
          keyword,
        },
      })),
    }),
    osVersions: build.query({
      queryFn: withCreativeServerUrl(({ adslot }) => ({
        method: 'GET',
        url: '/v1/on-demand/fetch-os-versions',
        params: {
          adslot,
        },
      })),
    }),
    uploadAsset: build.mutation({
      queryFn: withCreativeServerUrl(
        ({
          testId,
          type,
          asset,
          assetType,
          deviceId,
          country,
          osv,
          activeTime,
          tvServerEnvironment,
        }) => {
          const formData = new FormData();

          if (assetType === 'primaryAsset') {
            formData.append('primaryAsset', asset);
          }
          if (assetType === 'destinationAsset') {
            formData.append('destinationAsset', asset);
          }
          if (assetType === 'secondaryAsset') {
            formData.append('secondaryAsset', asset);
          }
          if (assetType === 'clickableVideoAsset') {
            formData.append('clickableVideoAsset', asset);
          }

          const params: Record<string, any> = {
            testId,
            type,
          };
          if (deviceId) {
            params.deviceId = deviceId;
            params.country = country;
            params.osv = osv;
            params.activeTime = activeTime;
          }
          if (tvServerEnvironment) {
            params.tvServerEnvironment = tvServerEnvironment;
          }

          return {
            method: 'POST',
            url: '/v1/on-demand/upload-asset',
            params,
            body: formData,
          };
        },
      ),
    }),
    setupCreativeSelfServe: build.mutation({
      queryFn: withCreativeServerUrl(payload => ({
        method: 'POST',
        url: '/v1/on-demand/setup-creative-self-serve',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      })),
    }),
    setupCreativeAdsure: build.mutation({
      queryFn: withCreativeServerUrl(payload => ({
        method: 'POST',
        url: '/v1/on-demand/setup-creative-adsure',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      })),
    }),
    activatePlacement: build.mutation({
      queryFn: withCreativeServerUrl(
        ({
          campaignId,
          placementId,
          activate,
          testId,
          tvServerEnvironment,
        }) => ({
          method: 'POST',
          url: '/v1/on-demand/activate-placement',
          params: {
            campaignId,
            placementId,
            activate,
            testId,
            ...(tvServerEnvironment ? { tvServerEnvironment } : {}),
          },
        }),
      ),
    }),
    generateAdsureTests: build.mutation({
      queryFn: withCreativeServerUrl(payload => ({
        method: 'POST',
        url: '/v1/on-demand/generate-ondemand-tests',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      })),
    }),
    bulkCreateAdsureTests: build.mutation({
      queryFn: withCreativeServerUrl(
        payload => ({
          method: 'POST',
          url: '/v1/on-demand/bulk-create-adsure-tests',
          headers: {
            'Content-Type': 'application/json',
          },
          body: payload,
        }),
      ),
    }),
    validateCreative: build.mutation({
      queryFn: withCreativeServerUrl(
        ({ campaignId, creativeId, country, os, tvServerEnvironment }) => ({
          method: 'GET',
          url: '/v1/on-demand/validate-creative-metadata',
          params: {
            cid: campaignId,
            crid: creativeId,
            queryCountry: country,
            os,
            ...(tvServerEnvironment ? { tvServerEnvironment } : {}),
          },
        }),
      ),
    }),
    validateAdslot: build.mutation({
      queryFn: withCreativeServerUrl(({ platform, height, width, os }) => ({
        method: 'POST',
        url: '/v1/on-demand/validate-adslot',
        params: {
          platform,
        },
        body: {
          dimension: {
            height,
            width,
          },
          os,
        },
      })),
    }),
    setDeviceLock: build.mutation({
      queryFn: withCreativeServerUrl(({ deviceId, lockStatus }) => ({
        method: 'POST',
        url: '/v1/on-demand/device-lock',
        body: {
          deviceId,
          lockStatus,
        },
      })),
    }),
    getActiveTest: build.mutation({
      queryFn: withCreativeServerUrl(({ podId }) => ({
        method: 'GET',
        url: '/v1/on-demand/active-on-demand-test',
        params: {
          podId,
        },
      })),
    }),
    getWebTesterMetadata: build.mutation({
      queryFn: withCreativeServerUrl(() => ({
        method: 'GET',
        url: '/v1/on-demand/web-tester-metadata',
      })),
    }),
    fetchStreamMetadata: build.mutation({
      queryFn: withCreativeServerUrl(payload => ({
        method: 'POST',
        url: '/v1/on-demand/stream-metadata',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      })),
    }),
    generateWebTestCases: build.mutation({
      queryFn: withCreativeServerUrl(({ metadata }) => ({
        method: 'POST',
        url: '/v1/on-demand/generate-web-test-cases',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { metadata },
      })),
    }),
    adsureMonitoringPods: build.query({
      queryFn: withCreativeServerUrl(() => ({
        method: 'GET',
        url: '/v1/adsure/monitoring/podList',
      })),
    }),
    updateAdsurePodStatus: build.mutation({
      queryFn: withCreativeServerUrl(({ pod, status }) => ({
        method: 'POST',
        url: '/v1/adsure/monitoring/updatePodStatus',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          pod,
          status,
        },
      })),
    }),
    wakeupCreativeAdminPod: build.mutation({
      queryFn: withCreativeServerUrl(({ pod }) => ({
        method: 'POST',
        url: `/v1/admin/pods/${encodeURIComponent(pod)}/actions/wakeup`,
        headers: {
          'Content-Type': 'application/json',
        },
        body: {},
      })),
    }),
    readCreativeAdminPodConfiguration: build.query({
      queryFn: withCreativeServerUrl((pod: string) => ({
        method: 'GET',
        url: `/v1/admin/pods/${encodeURIComponent(pod)}/configuration`,
      })),
    }),
    updateCreativeAdminPodConfiguration: build.mutation({
      queryFn: withCreativeServerUrl(
        ({ pod, configuration, restartAfterUpdate }) => ({
          method: 'PUT',
          url: `/v1/admin/pods/${encodeURIComponent(pod)}/configuration`,
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            configuration,
            restartAfterUpdate,
          },
        }),
      ),
    }),
    restartCreativeAdminPodProcess: build.mutation({
      queryFn: withCreativeServerUrl(({ pod, delayMs }) => ({
        method: 'POST',
        url: `/v1/admin/pods/${encodeURIComponent(
          pod,
        )}/actions/restart-pod-process`,
        headers: {
          'Content-Type': 'application/json',
        },
        body: delayMs ? { delayMs } : {},
      })),
    }),
  }),
});

export const {
  useAppNamesQuery,
  useAdslotsQuery,
  useActivatePlacementMutation,
  useContentPartnersQuery,
  useUploadAssetMutation,
  useSetupCreativeSelfServeMutation,
  useGetActiveTestMutation,
  useValidateCreativeMutation,
  useValidateAdslotMutation,
  useSetupCreativeAdsureMutation,
  useGenerateAdsureTestsMutation,
  useBulkCreateAdsureTestsMutation,
  useOsVersionsQuery,
  useSetDeviceLockMutation,
  useGetWebTesterMetadataMutation,
  useFetchStreamMetadataMutation,
  useGenerateWebTestCasesMutation,
  useAdsureMonitoringPodsQuery,
  useLazyReadCreativeAdminPodConfigurationQuery,
  useRestartCreativeAdminPodProcessMutation,
  useWakeupCreativeAdminPodMutation,
  useUpdateAdsurePodStatusMutation,
  useUpdateCreativeAdminPodConfigurationMutation,
} = creativeServerApi;
